import { useEffect, useMemo, useState } from "react";
import api, { apiErrorMessage, formatPLN } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Calendar, Layers } from "lucide-react";

/**
 * Helpers to compute quick date-range presets (returns {from, to} as YYYY-MM-DD).
 */
function fmt(d) {
  return d.toISOString().slice(0, 10);
}
function quickRanges() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const firstOfMonth = new Date(y, m, 1);
  const lastOfMonth = new Date(y, m + 1, 0);
  const firstOfPrevMonth = new Date(y, m - 1, 1);
  const lastOfPrevMonth = new Date(y, m, 0);
  const quarter = Math.floor(m / 3);
  const firstOfQuarter = new Date(y, quarter * 3, 1);
  const lastOfQuarter = new Date(y, quarter * 3 + 3, 0);
  const firstOfYear = new Date(y, 0, 1);
  const lastOfYear = new Date(y, 11, 31);
  return [
    { id: "current_month", label: "Bieżący miesiąc", from: fmt(firstOfMonth), to: fmt(lastOfMonth) },
    { id: "prev_month", label: "Poprzedni miesiąc", from: fmt(firstOfPrevMonth), to: fmt(lastOfPrevMonth) },
    { id: "current_quarter", label: "Bieżący kwartał", from: fmt(firstOfQuarter), to: fmt(lastOfQuarter) },
    { id: "current_year", label: "Bieżący rok", from: fmt(firstOfYear), to: fmt(lastOfYear) },
  ];
}

function fmtRangePl(from, to) {
  const f = (s) => {
    const [y, m, d] = s.split("-");
    return `${d}.${m}.${y}`;
  };
  return `${f(from)} – ${f(to)}`;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function ManagerReports() {
  const [sites, setSites] = useState([]);
  const presets = useMemo(() => quickRanges(), []);
  const [activePreset, setActivePreset] = useState("current_month");
  const [dateFrom, setDateFrom] = useState(presets[0].from);
  const [dateTo, setDateTo] = useState(presets[0].to);
  const [downloading, setDownloading] = useState({});
  const [consolidating, setConsolidating] = useState(false);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSites(r.data.sites || []));
  }, []);

  const setPreset = (p) => {
    setActivePreset(p.id);
    setDateFrom(p.from);
    setDateTo(p.to);
  };

  const onDateChange = (which, value) => {
    if (which === "from") setDateFrom(value);
    else setDateTo(value);
    setActivePreset("custom");
  };

  const downloadSite = async (site) => {
    if (dateTo < dateFrom) {
      toast.error("Data końcowa musi być >= początkowej");
      return;
    }
    setDownloading((d) => ({ ...d, [site.id]: true }));
    try {
      const res = await api.get(`/sites/${site.id}/report-range`, {
        params: { date_from: dateFrom, date_to: dateTo },
        responseType: "blob",
      });
      const safe = site.name.replace(/\s+/g, "_");
      triggerDownload(res.data, `raport_${safe}_${dateFrom}_${dateTo}.xlsx`);
      toast.success("Raport pobrany");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Nie udało się pobrać raportu"));
    } finally {
      setDownloading((d) => ({ ...d, [site.id]: false }));
    }
  };

  const downloadConsolidated = async () => {
    if (dateTo < dateFrom) {
      toast.error("Data końcowa musi być >= początkowej");
      return;
    }
    setConsolidating(true);
    try {
      const res = await api.get(`/reports/consolidated`, {
        params: { date_from: dateFrom, date_to: dateTo },
        responseType: "blob",
      });
      triggerDownload(res.data, `raport_skonsolidowany_${dateFrom}_${dateTo}.xlsx`);
      toast.success("Raport skonsolidowany pobrany");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Nie udało się pobrać raportu skonsolidowanego"));
    } finally {
      setConsolidating(false);
    }
  };

  return (
    <div>
      <PageHeader
        testId="manager-reports"
        title="Raporty"
        subtitle="Wygeneruj zestawienie zamówień (XLSX) dla wybranego zakresu dat"
      />

      <div className="ol-card p-5 mb-6">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5" /> Zakres raportu
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {presets.map((p) => (
            <button
              key={p.id}
              onClick={() => setPreset(p)}
              data-testid={`preset-${p.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                activePreset === p.id
                  ? "bg-[#FFF4ED] border-[#FF6B1A] text-[#CC4B00]"
                  : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
            >
              {p.label}
            </button>
          ))}
          {activePreset === "custom" && (
            <span className="px-3 py-1.5 rounded-full text-xs font-semibold border bg-[#FFF4ED] border-[#FF6B1A] text-[#CC4B00]">
              Własny zakres
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Od dnia</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => onDateChange("from", e.target.value)}
              data-testid="report-date-from"
              className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white focus:border-[#FF6B1A] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Do dnia</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => onDateChange("to", e.target.value)}
              data-testid="report-date-to"
              className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white focus:border-[#FF6B1A] focus:outline-none"
            />
          </div>
          <p className="text-xs text-slate-500 ml-auto self-center">Wybrano: <strong className="text-slate-900">{fmtRangePl(dateFrom, dateTo)}</strong></p>
        </div>
      </div>

      <div className="ol-card p-5 mb-6 bg-gradient-to-br from-[#FFF4ED] to-white border-[#FFD4B8]">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-[#FF6B1A] flex items-center justify-center flex-shrink-0">
            <Layers className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-900 mb-1">Raport skonsolidowany</p>
            <p className="text-xs text-slate-600 mb-3">
              Jeden XLSX z wszystkimi {sites.length === 1 ? "Twoją budową" : `Twoimi ${sites.length} budowami`} —
              osobna zakładka per budowa + arkusz &quot;Podsumowanie&quot; z sumą wydatków.
            </p>
            <button
              onClick={downloadConsolidated}
              disabled={consolidating || sites.length === 0}
              data-testid="download-consolidated"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] disabled:opacity-60 text-white text-sm font-semibold transition-colors"
            >
              <Download className="w-4 h-4" />
              {consolidating ? "Generuję…" : "Pobierz raport skonsolidowany"}
            </button>
          </div>
        </div>
      </div>

      <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Raporty per budowa</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sites.length === 0 && (
          <div className="ol-card p-8 text-center text-sm text-slate-500 md:col-span-2">
            Nie masz przypisanych budów.
          </div>
        )}
        {sites.map((s) => {
          const over = s.remaining < 0;
          return (
            <div key={s.id} className="ol-card p-5" data-testid={`report-site-${s.id}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-md bg-[#FFF4ED] flex items-center justify-center flex-shrink-0">
                  <FileSpreadsheet className="w-5 h-5 text-[#FF6B1A]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-900">{s.name}</p>
                  <p className="text-xs text-slate-500">NDI</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm mb-4 pt-3 border-t border-slate-100">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">Budżet</p>
                  <p className="font-semibold text-slate-900">{formatPLN(s.monthly_budget)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">Pozostało (bieżący m.)</p>
                  <p className={`font-semibold ${over ? "text-red-600" : "text-emerald-600"}`}>
                    {formatPLN(s.remaining)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => downloadSite(s)}
                disabled={downloading[s.id]}
                data-testid={`download-report-${s.id}`}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-[#FF6B1A] text-[#CC4B00] hover:bg-[#FFF4ED] disabled:opacity-60 text-sm font-semibold transition-colors"
              >
                <Download className="w-4 h-4" />
                {downloading[s.id] ? "Generuję…" : "Pobierz raport"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
